import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useTranslation } from 'react-i18next';
import { TokenHelper } from '../../TokenHelper';
import { useMsal } from '@azure/msal-react';
import { dataActionCreators, DataState, Project, ProjectAssigment, TimeSheetEntry } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { Button, MessageBar, MessageBarBody, Text } from '@fluentui/react-components';
import { Dismiss24Filled, Edit24Filled, ArrowUpload24Filled, ArrowDownload24Filled } from "@fluentui/react-icons";
import { useQuickViewStyles } from '../Styles/QuickView';
import { DownloadFileAndSave, DownloadSPOLinkAndNav } from '../Tools/FileDownloadHelper';
import { SharePointSVG } from '../Resources/SharePointSVG';
import { DataHelpers } from '../../DataHelpers';
import { useConsultants, useCustomers, useProjects, useProjectsAssigments, useRigths } from '../Tools/ObjectLoader';

export const TimeSheetQuickView = () => {
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const timesheetQuickView = useSelector<ApplicationState, TimeSheetEntry | null>((state: ApplicationState) => state.data.timesheetQuickView);
    const { customers } = useCustomers();
    const { projectsAssigments } = useProjectsAssigments();
    const { projects } = useProjects();
    const { consultants } = useConsultants();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const viewDebugInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewDebugInfo);
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const styles = useQuickViewStyles();
    const { isAdmin } = useRigths();

    const getPriceTypeSelectionString = (s: string): string => {
        switch (s) {
            case "H": return t('Hours');
            case "D": return t('Days');
            case "M": return t('Month');
            case "F": return t('Fixed');
        }
        return "Unknown";
    };

    if (timesheetQuickView && projectsAssigments && projects && customers && consultants) {
        const pa = projectsAssigments.find(e => e.projectAssignmentid === timesheetQuickView.projectAssignmentid);
        const p = pa ? projects.find(e => e.projectid === pa.projectid) : undefined;
        const c = p ? customers.find(e => e.customerid === p.customerid) : undefined;
        const co = pa ? consultants.find(e => e.consultantid === pa.consultantid) : undefined;
        const i = DataHelpers.GetInvoiceFromInvoiceid(data, timesheetQuickView.invoiceid);

        const priceConsultant: number = pa ? pa.consultantPrice * timesheetQuickView.count : 0;
        const priceCustomer: number = pa ? pa.customerPrice * timesheetQuickView.count : 0;

        return (
            <div className={styles.root} >
                <div className={styles.titleAndOps}>
                    <h2>{"TimeSheet : " + (timesheetQuickView.date ? new Date(timesheetQuickView.date).toLocaleDateString() : "-")}</h2>
                    <div className={styles.titleAndOpsButtons}>
                        {((isAdmin
                            || timesheetQuickView.invoiceid === null)) && ((i === null || (!i.emailSent && i.payment === 0)))
                            && <Button icon={<Edit24Filled />} shape="circular" title={t('Edit')} onClick={() => dispatch(dataActionCreators.requestTimeSheetEntryEdition(timesheetQuickView))} />}
                        <Button icon={<Dismiss24Filled />} shape="circular" title="Close" onClick={() => dispatch(dataActionCreators.requestClearQuickView())} />
                    </div>
                </div>
                {viewDebugInfo &&
                    <>
                        <h2>Id</h2>
                        <Text truncate>{timesheetQuickView.timesheetEntryid}</Text>
                    </>
                }
                <h2>TimeSheet Doc</h2>
                <div className={styles.InlineRow}>
                    {timesheetQuickView.timeSheetFileExist
                        ? <>
                            {isAdmin &&
                                <Button shape="circular" title={t('ViewInSharePoint')} icon={<SharePointSVG />}
                                    onClick={() => {
                                        TokenHelper.GetToken(instance, teamsState)
                                            .then(token => {
                                                if (token !== null && timesheetQuickView.timesheetEntryid) {
                                                    DownloadSPOLinkAndNav('api/TimeSheets/' + timesheetQuickView.timesheetEntryid + "/DownloadSPOLink", token);
                                                }
                                            });
                                    }
                                    } />
                            }
                            <Button shape="circular" title="Download" icon={<ArrowDownload24Filled />}
                                onClick={() => {
                                    TokenHelper.GetToken(instance, teamsState)
                                        .then(token => {
                                            if (token !== null && timesheetQuickView.timesheetEntryid) {
                                                DownloadFileAndSave('api/TimeSheetFile/' + timesheetQuickView.timesheetEntryid, token);
                                            }
                                        });
                                }
                                } />

                        </>
                        : <MessageBar intent="warning">
                            <MessageBarBody>
                                {t('Missing')}
                            </MessageBarBody>
                        </MessageBar>
                    }
                    {((timesheetQuickView.invoiceid === undefined || timesheetQuickView.invoiceid === null || isAdmin) &&
                        (i === null || (!i.emailSent && i.payment === 0))) &&
                        <Button icon={<ArrowUpload24Filled />} shape="circular" title="Upload" onClick={() => dispatch(dataActionCreators.requestFileUploadTimeSheet(timesheetQuickView))} />
                    }
                </div>
                {isAdmin &&
                    <><h2>{t('Consultant')}</h2>
                        <Text truncate>{co && co.displayName ? co.displayName : "?"}</Text>
                    </>
                }
                <h2>{t('Customer')}</h2>
                <Text>{c && c.displayName ? c.displayName : "?"}</Text>
                <h2>{"Project/" + t('ProjectsAssigment')}</h2>
                <Text>{(p && p.displayName ? p.displayName.trim() : "?") + " / " + (pa && pa.displayName ? pa.displayName.trim() : "?")}</Text>
                <h2>Count</h2>
                <Text>{timesheetQuickView.count + " " + getPriceTypeSelectionString(pa ? pa.unitOfTime : "?")}</Text>
                <h2>€</h2>
                <Text>{t('Consultant') + " = " + priceConsultant.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                {isAdmin && <>
                    <Text>{t('Customer') + " = " + priceCustomer.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                    <Text>{t('Margin') + " = " + (priceCustomer - priceConsultant).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                </>
                }
            </div>
        );
    }
    return null;
};